import React from "react";
import { DoorClosed, Sparkles } from "lucide-react";

/**
 * RoomReadinessCard — a small tile grid, one tile per room. No hallway
 * illustration; rooms are information about the boutique, not layout.
 *
 * State comes from `daily_logs.rooms` merged with `room_resets` completion
 * evidence. If a room is `assigned` but has no reset today it shows as
 * pending; a reset entry today marks it as reset (ready). Unassigned
 * rooms appear as quiet tiles.
 */

const STATUS_LABEL = {
  reset: "Reset today",
  pending: "Awaiting reset",
  not_assigned: "Unassigned",
};

const STATUS_STYLE = {
  reset:        "border-emerald-200 bg-emerald-50/60",
  pending:      "border-amber-200 bg-amber-50/60",
  not_assigned: "border-stone-200 bg-stone-50/40",
};

const STATUS_DOT = {
  reset: "bg-emerald-500",
  pending: "bg-amber-400",
  not_assigned: "bg-stone-300",
};

export default function RoomReadinessCard({ tiles }) {
  const list = tiles || [];
  const pending = list.filter((t) => t.reset_status === "pending").length;
  const reset = list.filter((t) => t.reset_status === "reset").length;

  return (
    <section
      className="bg-white subtle-border rounded-2xl p-8 card-shadow"
      data-testid="room-readiness-card"
      aria-labelledby="room-readiness-title"
    >
      <header className="flex items-baseline justify-between mb-5">
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400">Guide Journey</div>
          <h2
            id="room-readiness-title"
            className="font-serif-display text-2xl text-stone-900 mt-1"
            style={{ fontWeight: 400 }}
          >
            Room readiness
          </h2>
        </div>
        <div className="text-[10px] label-eyebrow text-stone-400">
          {list.length === 0
            ? "no rooms yet"
            : `${reset} reset · ${pending} pending`}
        </div>
      </header>

      {list.length === 0 ? (
        <EmptyState />
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="room-tiles"
        >
          {list.map((t) => (
            <RoomTile key={t.room} tile={t} />
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-xl bg-stone-50/60 subtle-border py-6 px-6 text-sm text-stone-500 text-center leading-relaxed"
      data-testid="room-readiness-empty"
    >
      Rooms will appear here as they're set for today.
    </div>
  );
}

function RoomTile({ tile }) {
  const status = tile.reset_status;
  return (
    <div
      className={`rounded-xl border p-4 subtle-border transition-colors ${STATUS_STYLE[status] || ""}`}
      data-testid={`room-tile-${tile.room}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-serif-display text-2xl text-stone-900" style={{ fontWeight: 400 }}>
          {tile.room}
        </div>
        <span
          className={`w-2 h-2 rounded-full ${STATUS_DOT[status] || "bg-stone-300"}`}
          aria-hidden="true"
        />
      </div>
      <div className="text-[10px] label-eyebrow text-stone-400">{STATUS_LABEL[status] || status}</div>
      {tile.mt_name && (
        <div className="text-sm text-stone-800 truncate mt-1">{tile.mt_name}</div>
      )}
      {tile.room_type && (
        <div className="text-[10px] text-stone-400 mt-0.5">{tile.room_type}</div>
      )}
    </div>
  );
}
